import { useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

const PARTICLE_TYPES = {
  coins: { emoji: '🪙', size: 24 },
  stars: { emoji: '⭐', size: 20 },
  hearts: { emoji: '💖', size: 22 },
  sparkles: { emoji: '✨', size: 18 },
  fire: { emoji: '🔥', size: 20 },
  lightning: { emoji: '⚡', size: 22 },
};

const Particles = ({
  type = 'stars',
  count = 20,
  duration = 2000,
  spread = 100,
  gravity = 0.3,
  velocity = null,
  direction = 'up',
  onComplete,
  trigger = true,
  customEmoji,
}) => {
  const containerRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);

  const config = PARTICLE_TYPES[type] || PARTICLE_TYPES.stars;
  const emoji = customEmoji || config.emoji;
  const baseVelocity = velocity !== null ? velocity : (4 + Math.random() * 6);

  const createParticle = useCallback(() => {
    const angle = direction === 'up'
      ? -Math.PI / 2 + (Math.random() - 0.5) * (spread * Math.PI / 180)
      : Math.random() * Math.PI * 2;

    const v = typeof baseVelocity === 'number' ? baseVelocity * (0.5 + Math.random() * 0.5) : (4 + Math.random() * 6);

    return {
      x: 50,
      y: 50,
      vx: Math.cos(angle) * v,
      vy: Math.sin(angle) * v,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 15,
      scale: 0.5 + Math.random() * 0.5,
      opacity: 1,
    };
  }, [spread, direction, baseVelocity]);

  const animate = useCallback((startTime) => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    particlesRef.current.forEach((p, i) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += gravity;
      p.rotation += p.rotationSpeed;
      p.opacity = 1 - progress;

      const el = containerRef.current?.children[i];
      if (el) {
        el.style.transform = `translate(${p.x}%, ${p.y}%) rotate(${p.rotation}deg) scale(${p.scale})`;
        el.style.opacity = p.opacity;
      }
    });

    if (progress < 1) {
      animationRef.current = requestAnimationFrame(() => animate(startTime));
    } else {
      onComplete?.();
    }
  }, [duration, gravity, onComplete]);

  useEffect(() => {
    if (!trigger) return;

    particlesRef.current = Array.from({ length: count }, createParticle);

    const startTime = Date.now();
    animate(startTime);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [trigger, count, createParticle, animate]);

  if (!trigger) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            fontSize: config.size,
            willChange: 'transform, opacity',
          }}
        >
          {emoji}
        </Box>
      ))}
    </Box>
  );
};

export default Particles;
